import { MessageDescriptor } from 'react-intl';

import messages from './messages';

export const isEnabled = () =>
  process.env.PROPERTY_ASSESSMENT_V2_ENABLED === 'true';

export enum GROUP {
  GAS = 'GAS',
}

export enum SECTION {
  GAS = 'GAS',
  GAS_SUPPLY = 'GAS_SUPPLY',
  GAS_REGISTER_1 = 'GAS_REGISTER_1',
  GAS_REGISTER_2 = 'GAS_REGISTER_2',
  WATER_HEATER_1 = 'WATER_HEATER_1',
  WATER_HEATER_2 = 'WATER_HEATER_2',
}

export enum SLUG {
  GAS = 'gas',
  GAS_SUPPLY = 'abastecimento-de-gas',
  GAS_REGISTER_1 = 'registro-de-gas-1',
  GAS_REGISTER_2 = 'registro-de-gas-2',
  WATER_HEATER_1 = 'aquecedor-de-agua-1',
  WATER_HEATER_2 = 'aquecedor-de-agua-2',
}

export enum QUESTION {
  GAS_SUPPLY_TYPE = 'GAS_SUPPLY_TYPE',
  GAS_REGISTER_LOCATION = 'GAS_REGISTER_LOCATION',
  GAS_REGISTER_NEAR_WINDOWS_DOORS = 'GAS_REGISTER_NEAR_WINDOWS_DOORS',
  GAS_REGISTER_NEAR_ELECTRICAL_INSTALLATIONS = 'GAS_REGISTER_NEAR_ELECTRICAL_INSTALLATIONS',
  GAS_PIPE_COLOR = 'GAS_PIPE_COLOR',
  GAS_PIPE_IS_LABELED = 'GAS_PIPE_IS_LABELED',
  GAS_REGISTER_IN_ENCLOSED_SPACE = 'GAS_REGISTER_IN_ENCLOSED_SPACE',
  GAS_REGISTER_CONSERVATION = 'GAS_REGISTER_CONSERVATION',
  WATER_HEATER_INSTALLED = 'WATER_HEATER_INSTALLED',
  WATER_HEATER_LOCATION = 'WATER_HEATER_LOCATION',
  WATER_HEATER_NEAR_WINDOWS_DOORS = 'WATER_HEATER_NEAR_WINDOWS_DOORS',
  WATER_HEATER_CONSERVATION = 'WATER_HEATER_CONSERVATION',
}

export enum COMMENT {
  WATER_HEATER_LOCATION_COMMENT = 'WATER_HEATER_LOCATION_COMMENT',
}

export enum ANSWER {
  PIPED_GAS = 'PIPED_GAS',
  GAS_CANISTER = 'GAS_CANISTER',
  GAS_PIPE_COLOR_YELLOW = 'GAS_PIPE_COLOR_YELLOW',
  GAS_PIPE_COLOR_ALUMINUM = 'GAS_PIPE_COLOR_ALUMINUM',

  KITCHEN = 'KITCHEN',
  BATHROOM = 'BATHROOM',
  SERVICE_AREA = 'SERVICE_AREA',

  NEEDS_REPAIR = 'NEEDS_REPAIR',
  MANY_SIGNS_OF_USE = 'MANY_SIGNS_OF_USE',
  FEW_SIGNS_OF_USE = 'FEW_SIGNS_OF_USE',
  NEW_ITEM = 'NEW_ITEM',

  YES = 'YES',
  NO = 'NO',
  OTHER = 'OTHER',
  DOES_NOT_HAVE = 'DOES_NOT_HAVE',
}

export enum INPUT {
  TEXT = 'TEXT',
  CHIP_SINGLE = 'CHIP_SINGLE',
  CHIP_MULTIPLE = 'CHIP_MULTIPLE',
  RADIO = 'RADIO',
}

type TGroupBySectionKey = `${SECTION}`;
type TGroupBySection = Readonly<Record<TGroupBySectionKey, GROUP>>;

export const GROUP_BY_SECTION: TGroupBySection = Object.freeze({
  [SECTION.GAS]: GROUP.GAS,
  [SECTION.GAS_SUPPLY]: GROUP.GAS,
  [SECTION.GAS_REGISTER_1]: GROUP.GAS,
  [SECTION.GAS_REGISTER_2]: GROUP.GAS,
  [SECTION.WATER_HEATER_1]: GROUP.GAS,
  [SECTION.WATER_HEATER_2]: GROUP.GAS,
});

type TSlugBySectionKey = `${SECTION}`;
type TSlugBySection = Readonly<Record<TSlugBySectionKey, SLUG>>;

export const SLUG_BY_SECTION: TSlugBySection = Object.freeze({
  [SECTION.GAS]: SLUG.GAS,
  [SECTION.GAS_SUPPLY]: SLUG.GAS_SUPPLY,
  [SECTION.GAS_REGISTER_1]: SLUG.GAS_REGISTER_1,
  [SECTION.GAS_REGISTER_2]: SLUG.GAS_REGISTER_2,
  [SECTION.WATER_HEATER_1]: SLUG.WATER_HEATER_1,
  [SECTION.WATER_HEATER_2]: SLUG.WATER_HEATER_2,
});

type TSectionBySlugKey = `${SLUG}`;
type TSectionBySlug = Readonly<Record<TSectionBySlugKey, SECTION>>;

export const SECTION_BY_SLUG: TSectionBySlug = Object.freeze({
  [SLUG.GAS]: SECTION.GAS,
  [SLUG.GAS_SUPPLY]: SECTION.GAS_SUPPLY,
  [SLUG.GAS_REGISTER_1]: SECTION.GAS_REGISTER_1,
  [SLUG.GAS_REGISTER_2]: SECTION.GAS_REGISTER_2,
  [SLUG.WATER_HEATER_1]: SECTION.WATER_HEATER_1,
  [SLUG.WATER_HEATER_2]: SECTION.WATER_HEATER_2,
});

type TPageViewedEventBySectionKey = `${SECTION}`;
type TPageViewedEventBySection = Readonly<
  Record<TPageViewedEventBySectionKey, string>
>;

export const PAGE_VIEWED_EVENT_BY_SECTION: TPageViewedEventBySection =
  Object.freeze({
    [SECTION.GAS]: 'checklist_gas_page_viewed',
    [SECTION.GAS_SUPPLY]: 'checklist_gas_supply_page_viewed',
    [SECTION.GAS_REGISTER_1]: 'checklist_gas_register_1_page_viewed',
    [SECTION.GAS_REGISTER_2]: 'checklist_gas_register_2_page_viewed',
    [SECTION.WATER_HEATER_1]: 'checklist_water_heater_1_page_viewed',
    [SECTION.WATER_HEATER_2]: 'checklist_water_heater_2_page_viewed',
  });

type TVisibleIf = {
  section: `${SECTION}`;
  question: `${QUESTION}`;
  answer: `${ANSWER}`;
};

type TComment = {
  id: `${COMMENT}`;
  visibleIf?: TVisibleIf;
};

type ISectionItem = {
  input: `${INPUT}`;
  question: `${QUESTION}`;
  answers?: `${ANSWER}`[];
  comment?: TComment;
  visibleIf?: TVisibleIf;
};

type TSectionItemsKey = `${SECTION}`;
type TSectionItems = Readonly<Record<TSectionItemsKey, ISectionItem[]>>;

export const SECTION_ITEMS: TSectionItems = Object.freeze({
  [SECTION.GAS]: [],
  [SECTION.GAS_SUPPLY]: [
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.GAS_SUPPLY_TYPE,
      answers: [ANSWER.PIPED_GAS, ANSWER.GAS_CANISTER, ANSWER.DOES_NOT_HAVE],
    },
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.GAS_REGISTER_LOCATION,
      answers: [ANSWER.YES, ANSWER.NO],
    },
  ],
  [SECTION.GAS_REGISTER_1]: [
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.GAS_REGISTER_NEAR_WINDOWS_DOORS,
      answers: [ANSWER.YES, ANSWER.NO],
    },
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.GAS_REGISTER_NEAR_ELECTRICAL_INSTALLATIONS,
      answers: [ANSWER.YES, ANSWER.NO],
    },
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.GAS_PIPE_COLOR,
      answers: [
        ANSWER.GAS_PIPE_COLOR_YELLOW,
        ANSWER.GAS_PIPE_COLOR_ALUMINUM,
        ANSWER.OTHER,
      ],
    },
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.GAS_PIPE_IS_LABELED,
      answers: [ANSWER.YES, ANSWER.NO],
    },
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.GAS_REGISTER_IN_ENCLOSED_SPACE,
      answers: [ANSWER.YES, ANSWER.NO],
    },
  ],
  [SECTION.GAS_REGISTER_2]: [
    {
      input: INPUT.RADIO,
      question: QUESTION.GAS_REGISTER_CONSERVATION,
      answers: [
        ANSWER.NEEDS_REPAIR,
        ANSWER.MANY_SIGNS_OF_USE,
        ANSWER.FEW_SIGNS_OF_USE,
        ANSWER.NEW_ITEM,
      ],
    },
  ],
  [SECTION.WATER_HEATER_1]: [
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.WATER_HEATER_INSTALLED,
      answers: [ANSWER.YES, ANSWER.NO],
    },
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.WATER_HEATER_LOCATION,
      answers: [
        ANSWER.KITCHEN,
        ANSWER.BATHROOM,
        ANSWER.SERVICE_AREA,
        ANSWER.OTHER,
      ],
      comment: {
        id: COMMENT.WATER_HEATER_LOCATION_COMMENT,
        visibleIf: {
          section: SECTION.WATER_HEATER_1,
          question: QUESTION.WATER_HEATER_LOCATION,
          answer: ANSWER.OTHER,
        },
      },
    },
    {
      input: INPUT.CHIP_SINGLE,
      question: QUESTION.WATER_HEATER_NEAR_WINDOWS_DOORS,
      answers: [ANSWER.YES, ANSWER.NO],
    },
  ],
  [SECTION.WATER_HEATER_2]: [
    {
      input: INPUT.RADIO,
      question: QUESTION.WATER_HEATER_CONSERVATION,
      answers: [
        ANSWER.NEEDS_REPAIR,
        ANSWER.MANY_SIGNS_OF_USE,
        ANSWER.FEW_SIGNS_OF_USE,
        ANSWER.NEW_ITEM,
      ],
    },
  ],
});

interface IComponentProps {
  title: MessageDescriptor;
  description?: MessageDescriptor;
}

type TQuestionPropsKey = `${QUESTION}` | `${COMMENT}`;
type TQuestionProps = Readonly<Record<TQuestionPropsKey, IComponentProps>>;

export const QUESTION_PROPS: TQuestionProps = Object.freeze({
  [QUESTION.GAS_SUPPLY_TYPE]: {
    title: messages.gasSupplyTypeQuestion,
  },
  [QUESTION.GAS_REGISTER_LOCATION]: {
    title: messages.gasRegisterLocationQuestion,
  },
  [QUESTION.GAS_REGISTER_NEAR_WINDOWS_DOORS]: {
    title: messages.gasRegisterNearWindowsDoorsQuestion,
  },
  [QUESTION.GAS_REGISTER_NEAR_ELECTRICAL_INSTALLATIONS]: {
    title: messages.gasRegisterNearElectricalInstallationsQuestion,
  },
  [QUESTION.GAS_PIPE_COLOR]: {
    title: messages.gasPipeColorQuestion,
  },
  [QUESTION.GAS_PIPE_IS_LABELED]: {
    title: messages.gasPipeIsLabeledQuestion,
  },
  [QUESTION.GAS_REGISTER_IN_ENCLOSED_SPACE]: {
    title: messages.gasRegisterInEnclosedSpaceQuestion,
    description: messages.gasRegisterInEnclosedSpaceDescription,
  },
  [QUESTION.GAS_REGISTER_CONSERVATION]: {
    title: messages.gasRegisterConservationQuestion,
  },
  [QUESTION.WATER_HEATER_INSTALLED]: {
    title: messages.waterHeaterInstalledQuestion,
  },
  [QUESTION.WATER_HEATER_LOCATION]: {
    title: messages.waterHeaterLocationQuestion,
  },
  [COMMENT.WATER_HEATER_LOCATION_COMMENT]: {
    title: messages.commentQuestion,
    description: messages.commentDescription,
  },
  [QUESTION.WATER_HEATER_NEAR_WINDOWS_DOORS]: {
    title: messages.waterHeaterNearWindowsDoorsQuestion,
  },
  [QUESTION.WATER_HEATER_CONSERVATION]: {
    title: messages.waterHeaterConservationQuestion,
  },
});

type TAnswerPropsKey = `${ANSWER}`;
type TAnswerProps = Readonly<Record<TAnswerPropsKey, IComponentProps>>;

export const ANSWER_PROPS: TAnswerProps = Object.freeze({
  [ANSWER.PIPED_GAS]: {
    title: messages.pipedGas,
  },
  [ANSWER.GAS_CANISTER]: {
    title: messages.gasCanister,
  },
  [ANSWER.GAS_PIPE_COLOR_YELLOW]: {
    title: messages.gasPipeColorYellow,
  },
  [ANSWER.GAS_PIPE_COLOR_ALUMINUM]: {
    title: messages.gasPipeColorAluminum,
  },

  [ANSWER.KITCHEN]: {
    title: messages.kitchen,
  },
  [ANSWER.BATHROOM]: {
    title: messages.bathroom,
  },
  [ANSWER.SERVICE_AREA]: {
    title: messages.serviceArea,
  },

  [ANSWER.NEEDS_REPAIR]: {
    title: messages.needsRepair,
  },
  [ANSWER.MANY_SIGNS_OF_USE]: {
    title: messages.manySignsOfUse,
  },
  [ANSWER.FEW_SIGNS_OF_USE]: {
    title: messages.fewSignsOfUse,
  },
  [ANSWER.NEW_ITEM]: {
    title: messages.newItem,
  },

  [ANSWER.YES]: {
    title: messages.yes,
  },
  [ANSWER.NO]: {
    title: messages.no,
  },
  [ANSWER.OTHER]: {
    title: messages.other,
  },
  [ANSWER.DOES_NOT_HAVE]: {
    title: messages.doesNotHave,
  },
});

interface ISectionProps extends IComponentProps {
  description?: MessageDescriptor;
  illustration?: string;
  isMainSection?: boolean;
}

type TSectionPropsKey = `${SECTION}`;
type TSectionProps = Readonly<Record<TSectionPropsKey, ISectionProps>>;

export const SECTION_PROPS: TSectionProps = Object.freeze({
  [SECTION.GAS]: {
    title: messages.gasTitle,
    description: messages.gasDescription,
    isMainSection: true,
  },
  [SECTION.GAS_SUPPLY]: {
    title: messages.gasSupplyTitle,
  },
  [SECTION.GAS_REGISTER_1]: {
    title: messages.gasRegisterTitle,
  },
  [SECTION.GAS_REGISTER_2]: {
    title: messages.gasRegisterTitle,
  },
  [SECTION.WATER_HEATER_1]: {
    title: messages.waterHeaterTitle,
  },
  [SECTION.WATER_HEATER_2]: {
    title: messages.waterHeaterTitle,
  },
});

export const NON_SAVEABLE_SECTIONS: `${SECTION}`[] = [SECTION.GAS];
